import { isPhoneComplete, phoneDigits } from '../../utils/phone';
import {
  suggestEmailDomain,
  validateRegisterEmail,
} from '../../utils/emailDomain';

export function buildAuthValidationCopy(t) {
  return {
    phoneRequired: t('auth.validation.phoneRequired'),
    phoneInvalid: t('auth.validation.phoneInvalid'),
    phoneExists: t('auth.validation.phoneExists'),
    phoneOrEmailRequired: t('auth.validation.phoneOrEmailRequired'),
    emailRequired: t('auth.validation.emailRequired'),
    emailInvalid: t('auth.validation.emailInvalid'),
    emailDomainInvalid: t('auth.validation.emailDomainInvalid')
      || 'Geçerli bir e-posta kullanın (gmail.com, hotmail.com, outlook.com, mynet.com, yahoo.com vb.)',
    emailExists: t('auth.validation.emailExists'),
    usernameRequired: t('auth.validation.usernameRequired') || 'Kullanıcı adı zorunlu',
    usernameShort: t('auth.validation.usernameShort') || 'Kullanıcı adı en az 3 karakter olmalı',
    usernameInvalid: t('auth.validation.usernameInvalid') || 'Kullanıcı adı geçersiz',
    passwordRequired: t('auth.validation.passwordRequired'),
    passwordShort: t('auth.validation.passwordShort'),
    rulesRequired: t('auth.register.rulesRequired'),
    registerFailed: t('auth.validation.registerFailed'),
    loginFieldsRequired: t('auth.validation.loginFieldsRequired'),
    loginInvalid: t('auth.validation.loginInvalid'),
    accountRequired: t('auth.validation.accountRequired'),
    accountShort: t('auth.validation.accountShort'),
    codeInvalid: t('auth.validation.codeInvalid'),
    codeRequired: t('auth.validation.codeRequired'),
    codeSendFailed: t('auth.validation.codeSendFailed'),
    codeThrottled: t('auth.validation.codeThrottled'),
    challengeExpired: t('auth.validation.challengeExpired'),
    serverUnreachable: t('auth.validation.serverUnreachable'),
    serverError: t('auth.validation.serverError'),
  };
}

function emailFieldError(email, copy, { required = false } = {}) {
  const code = validateRegisterEmail(email, { required });
  if (!code) return null;
  if (code === 'required') return copy.emailRequired;
  if (code === 'domain') {
    const hint = suggestEmailDomain(email);
    if (hint) {
      return `${copy.emailDomainInvalid} (${hint}?)`;
    }
    return copy.emailDomainInvalid;
  }
  return copy.emailInvalid;
}

export function validateRegistrationStep1({ username, email, password, ageRules }, copy) {
  const errors = {};
  const user = String(username || '').trim().toLowerCase();
  if (!user) errors.username = copy.usernameRequired || 'Kullanıcı adı zorunlu';
  else if (user.length < 3) errors.username = copy.usernameShort || 'Kullanıcı adı en az 3 karakter olmalı';
  else if (!/^[a-z0-9._-]{3,32}$/.test(user)) errors.username = copy.usernameInvalid || 'Kullanıcı adı geçersiz';

  const mailErr = emailFieldError(email, copy, { required: false });
  if (mailErr) errors.email = mailErr;

  if (!password) errors.password = copy.passwordRequired;
  else if (password.length < 8) errors.password = copy.passwordShort;

  if (!ageRules) errors.ageRules = copy.rulesRequired;
  return errors;
}

export function validateRegistrationStep2({
  firstName, lastName, gender, birthDay, birthMonth, birthYear, phone, address, email,
}, copy) {
  const errors = {};
  if (!String(firstName || '').trim()) errors.firstName = copy.firstNameRequired || 'Ad zorunlu';
  if (!String(lastName || '').trim()) errors.lastName = copy.lastNameRequired || 'Soyad zorunlu';
  if (!['male', 'female', 'unspecified'].includes(String(gender || ''))) {
    errors.gender = copy.genderRequired || 'Cinsiyet zorunlu';
  }
  if (!birthDay || !birthMonth || !birthYear) {
    errors.birthDay = copy.birthRequired || 'Doğum tarihi zorunlu';
  } else {
    const birth = `${birthYear}-${birthMonth}-${birthDay}`;
    const dt = new Date(`${birth}T00:00:00`);
    if (Number.isNaN(dt.getTime())) errors.birthDay = copy.birthInvalid || 'Doğum tarihi geçersiz';
  }

  const digits = phoneDigits(phone);
  const mail = String(email || '').trim().toLowerCase();
  if (digits.length) {
    if (!isPhoneComplete(phone)) errors.phone = copy.phoneInvalid;
  } else if (!mail) {
    errors.phone = copy.phoneOrEmailRequired || copy.phoneRequired;
  }

  const mailErr = emailFieldError(email, copy, { required: false });
  if (mailErr) errors.email = mailErr;

  if (!String(address || '').trim() || String(address).trim().length < 5) {
    errors.address = copy.addressRequired || 'Adres zorunlu';
  }
  return errors;
}

export function validateRegistration({ method, phone, email, password, ageRules }, copy) {
  const errors = {};

  if (method === 'email') {
    const mailErr = emailFieldError(email, copy, { required: true });
    if (mailErr) errors.email = mailErr;
  } else {
    const digits = phoneDigits(phone);
    if (!digits.length) {
      errors.phone = copy.phoneRequired;
    } else if (!isPhoneComplete(phone)) {
      errors.phone = copy.phoneInvalid;
    }
  }

  if (!password) {
    errors.password = copy.passwordRequired;
  } else if (password.length < 8) {
    errors.password = copy.passwordShort;
  }

  if (!ageRules) {
    errors.ageRules = copy.rulesRequired;
  }

  return errors;
}

export function mapAuthApiError(message, copy, fallback) {
  const API_ERROR_MAP = {
    'Telefon ve şifre zorunlu': { phone: copy.phoneRequired, password: copy.passwordRequired },
    'Telefon zorunlu': { phone: copy.phoneRequired },
    'E-posta veya telefon zorunlu': { phone: copy.phoneOrEmailRequired || copy.phoneRequired },
    'E-posta zorunlu': { email: copy.emailRequired },
    'Geçersiz e-posta adresi': { email: copy.emailInvalid },
    'Geçersiz e-posta alanı': { email: copy.emailDomainInvalid },
    'Geçerli bir telefon numarası girin': { phone: copy.phoneInvalid },
    'Şifre zorunlu': { password: copy.passwordRequired },
    'Şifre en az 6 karakter olmalı': { password: copy.passwordShort },
    'Şifre en az 8 karakter olmalı': { password: copy.passwordShort },
    'Bu telefon numarası zaten kayıtlı': { phone: copy.phoneExists },
    'Bu e-posta adresi zaten kayıtlı': { email: copy.emailExists },
    'Bu kullanıcı adı zaten alınmış': { username: copy.usernameExists || 'Bu kullanıcı adı zaten alınmış' },
    'Kullanıcı adı en az 3 karakter olmalı': { username: copy.usernameShort || 'Kullanıcı adı en az 3 karakter olmalı' },
    'Kullanıcı adı geçersiz': { username: copy.usernameInvalid || 'Kullanıcı adı geçersiz' },
    'Ad zorunlu': { firstName: 'Ad zorunlu' },
    'Soyad zorunlu': { lastName: 'Soyad zorunlu' },
    'Cinsiyet zorunlu': { gender: 'Cinsiyet zorunlu' },
    'Doğum tarihi zorunlu': { birthDay: 'Doğum tarihi zorunlu' },
    'Adres zorunlu': { address: 'Adres zorunlu' },
    'Kayıt oluşturulamadı': { form: copy.registerFailed },
    'Giriş bilgileri eksik': { form: copy.loginFieldsRequired },
    'Hesap numarası veya şifre hatalı': { form: copy.loginInvalid },
    'Telefon numarası veya şifre hatalı': { form: copy.loginInvalid },
    'Telefon veya şifre hatalı': { form: copy.loginInvalid },
    'Onay kodu hatalı': { form: copy.codeInvalid },
    'Onay kodu gerekli': { form: copy.codeRequired },
    'Kod gönderilemedi': { form: copy.codeSendFailed },
    'Kodu tekrar göndermek için biraz bekleyin': { form: copy.codeThrottled },
    'Doğrulama oturumu süresi doldu': { form: copy.challengeExpired },
    'E-posta veya şifre hatalı': { form: copy.loginInvalid },
    'Kullanıcı adı veya şifre hatalı': { form: copy.loginInvalid },
    'Giriş yapılamadı': { form: copy.serverError },
    'Sunucuya bağlanılamadı. npm run dev:server komutunu çalıştırın.': { form: copy.serverUnreachable },
  };
  const mapped = API_ERROR_MAP[message];
  if (mapped) return mapped;
  if (String(message || '').startsWith('Geçersiz e-posta alanı')) {
    return { email: message };
  }
  const msg = String(message || '');
  if (
    /Bağlantı hatası|Sunucu geçici|yanıt vermiyor|Failed to fetch|NetworkError|ulaşılamadı|bağlanılamadı/i.test(msg)
  ) {
    return { form: '' };
  }
  return { form: message || fallback };
}

export function validateLogin({ tab, loginValue, password }, copy) {
  const errors = {};

  if (tab === 'phone') {
    const digits = phoneDigits(loginValue);
    if (!digits.length) errors.login = copy.phoneRequired;
    else if (!isPhoneComplete(loginValue)) errors.login = copy.phoneInvalid;
  } else if (!String(loginValue || '').trim()) {
    errors.login = tab === 'email'
      ? copy.emailRequired
      : tab === 'username'
        ? (copy.usernameRequired || 'Kullanıcı adı zorunlu')
        : copy.accountRequired;
  } else if (tab === 'username') {
    const user = String(loginValue || '').trim().toLowerCase();
    if (user.length < 3) errors.login = copy.usernameShort || 'Kullanıcı adı en az 3 karakter olmalı';
    else if (!/^[a-z0-9._-]{3,32}$/.test(user)) {
      errors.login = copy.usernameInvalid || 'Kullanıcı adı geçersiz';
    }
  } else if (tab === 'account' && String(loginValue).replace(/\D/g, '').length < 6) {
    errors.login = copy.accountShort;
  }

  if (!password) errors.password = copy.passwordRequired;

  return errors;
}
